import {
  isSlotSplitNeeded,
  mergeOverlappingSlots,
  transformVoiceSpeedToFrontend,
  transformVolumeToFrontend,
} from "../../utils/setting-helpers";
import {
  emptySchedule,
  IAgentSettings,
  IOrderingConfig,
  ISchedule,
  ITimeSlot,
  IPrepTimeSchedule,
  IPrepTimeSlot,
  TimeString,
  DayOfWeek,
} from "../../types/agent-settings";
import {
  IAgentSettingsRequest,
  IScheduleRequest,
  IScheduleResponse,
  ITimeSlotResponse,
  IAgentSettingsResponse,
  IFAQRequest,
  IOrderingConfigRequest,
  IPrepTimeScheduleResponse,
  IPrepTimeScheduleRequest,
  IPrepTimeSlotResponse,
  IPrepTimeSlotRequest,
} from "../../types/api/agent-settings";
import {
  transformVoiceSpeedToBackend,
  transformVolumeToBackend,
} from "../../utils/setting-helpers";
import { IFAQ } from "../../types/faq";
import { IChangedSettings } from "../../types/agent-settings";
import { LOCAL_ID_PREFIX } from "../../components/feature/settings/faq/faq-constants";
import {
  minutesToISO8601Duration,
  ISO8601DurationToMinutes,
} from "@loman/utils";

export function toBackendSettings(
  settings: IChangedSettings
): Partial<IAgentSettingsRequest> {
  const backendSettings: Partial<IAgentSettingsRequest> = {};

  if (
    settings.allowAnonymousCalls !== undefined &&
    settings.allowAnonymousCalls !== null
  ) {
    backendSettings.allowAnon = settings.allowAnonymousCalls;
  }
  if (settings.ambientNoise !== undefined && settings.ambientNoise !== null) {
    backendSettings.ambientNoise = settings.ambientNoise;
  }
  if (
    settings.ambientNoiseVolume !== undefined &&
    settings.ambientNoiseVolume !== null
  ) {
    backendSettings.ambientNoiseVolume = transformVolumeToBackend(
      settings.ambientNoiseVolume
    );
  }
  if (settings.voiceId !== undefined && settings.voiceId !== null) {
    backendSettings.voiceId = settings.voiceId;
  }
  if (settings.voiceSpeed !== undefined && settings.voiceSpeed !== null) {
    backendSettings.voiceSpeed = transformVoiceSpeedToBackend(
      settings.voiceSpeed
    );
  }
  if (
    settings.greetingMessageOpen !== undefined &&
    settings.greetingMessageOpen !== null
  ) {
    backendSettings.greetingMessageOpen = settings.greetingMessageOpen;
  }
  if (
    settings.greetingMessageClosed !== undefined &&
    settings.greetingMessageClosed !== null
  ) {
    backendSettings.greetingMessageClosed = settings.greetingMessageClosed;
  }
  if (
    settings.greetingMessageTempClosed !== undefined &&
    settings.greetingMessageTempClosed !== null
  ) {
    backendSettings.greetingMessageTempClosed =
      settings.greetingMessageTempClosed;
  }
  if (settings.tempClosedUntil !== undefined) {
    backendSettings.tempClosedUntil = settings.tempClosedUntil;
  }
  if (settings.schedule !== undefined && settings.schedule !== null) {
    backendSettings.hours = toBackendSchedule(settings.schedule);
  }
  if (settings.orderingHours !== undefined && settings.orderingHours !== null) {
    backendSettings.hoursOrdering = toBackendSchedule(settings.orderingHours);
  }
  if (settings.orderingHours === null) {
    backendSettings.hoursOrdering = null;
  }
  if (settings.faqs !== undefined && settings.faqs !== null) {
    backendSettings.faqs = toBackendFaqs(settings.faqs);
  }
  if (
    settings.orderingConfig !== undefined &&
    settings.orderingConfig !== null
  ) {
    backendSettings.orderingConfig = toBackendOrderingConfig(
      settings.orderingConfig
    );
  }

  if (
    settings.deliveryPrepTime !== undefined &&
    settings.deliveryPrepTime !== null &&
    settings.deliveryPrepTime !== ""
  ) {
    backendSettings.deliveryPrepTime = minutesToISO8601Duration(
      parseInt(settings.deliveryPrepTime)
    );
  }
  if (
    settings.pickupPrepTime !== undefined &&
    settings.pickupPrepTime !== null &&
    settings.pickupPrepTime !== ""
  ) {
    backendSettings.pickupPrepTime = minutesToISO8601Duration(
      parseInt(settings.pickupPrepTime)
    );
  }
  if (settings.customPrepTimeEnabled !== undefined) {
    backendSettings.customPrepTimeEnabled =
      settings.customPrepTimeEnabled ?? false;
  }
  if (settings.customPrepTimeHours !== undefined) {
    backendSettings.customPrepTimeHours =
      toBackendPrepTimeSchedule(settings.customPrepTimeHours) ?? null;
  }

  return backendSettings;
}

export function toFrontendSettings(
  settings: IAgentSettingsResponse
): IAgentSettings {
  const customPrepSchedule = toFrontendPrepTimeSchedule(
    settings.customPrepTimeHours
  );
  const legacyPrepTime = settings?.orderingConfig?.prepTime
    ? ISO8601DurationToMinutes(settings.orderingConfig.prepTime).toString()
    : undefined;
  return {
    id: settings.id,
    name: settings.name,
    type: settings.type,
    address: settings.address,
    allowAnonymousCalls: settings.allowAnon,
    ambientNoise: settings.ambientNoise,
    ambientNoiseVolume: transformVolumeToFrontend(settings.ambientNoiseVolume),
    voiceId: settings.voiceId,
    voiceSpeed: transformVoiceSpeedToFrontend(settings.voiceSpeed),
    phoneNumber: settings.phoneNumber,
    fallbackPhoneNumber: settings.fallbackPhoneNumber,
    greetingMessageOpen: settings.greetingMessageOpen,
    greetingMessageClosed: settings.greetingMessageClosed,
    greetingMessageTempClosed: settings.greetingMessageTempClosed,
    schedule: toFrontendSchedule(settings.hours) ?? emptySchedule,
    orderingHours: toFrontendSchedule(settings.hoursOrdering) ?? null,
    tempClosedUntil: settings.tempClosedUntil,
    faqs: settings.faqs,
    stripeAccountId: settings.stripeAccountId,
    stripeChargesEnabled: settings.stripeChargesEnabled,
    streamConnectedAt: settings.streamConnectedAt,
    posProvider: settings.posProvider,
    orderingConfig: {
      exactTimeEstimate: settings?.orderingConfig?.exactTimeEstimate || false,
      prepTime: legacyPrepTime,
      allowScheduledOrders:
        settings?.orderingConfig?.allowScheduledOrders || "never",
      orderUntilClosed: settings?.orderingConfig?.orderUntilClosed || false,
      shouldTotal: settings?.orderingConfig?.shouldTotal || false,
      pickupModuleConfig: settings?.orderingConfig?.pickupModuleConfig || null,
      deliveryModuleConfig:
        settings?.orderingConfig?.deliveryModuleConfig || null,
      dynamicPrepTime: settings?.orderingConfig?.dynamicPrepTime,
    },
    deliveryPrepTime: settings.deliveryPrepTime
      ? ISO8601DurationToMinutes(settings.deliveryPrepTime).toString()
      : legacyPrepTime || null,
    pickupPrepTime: settings.pickupPrepTime
      ? ISO8601DurationToMinutes(settings.pickupPrepTime).toString()
      : legacyPrepTime || null,
    customPrepTimeEnabled: settings.customPrepTimeEnabled,
    customPrepTimeHours: customPrepSchedule,
  };
}

const daysOfTheWeek = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

function toBackendSchedule(schedule: ISchedule): IScheduleRequest {
  const newSchedule: IScheduleRequest = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
  for (const [index, day] of daysOfTheWeek.entries()) {
    const slots = schedule[day as keyof IScheduleRequest];
    const has24HourSlot = slots.some(
      (slot) =>
        slot.open &&
        slot.close &&
        slot.open === slot.close &&
        slot.open != "00:00"
    );
    if (has24HourSlot) {
      // If there's a 24-hour slot, only include that one
      const twentyFourSlot = slots.find(
        (slot) =>
          slot.open &&
          slot.close &&
          slot.open === slot.close &&
          slot.open !== "00:00"
      );
      if (twentyFourSlot && twentyFourSlot.open && twentyFourSlot.close) {
        newSchedule[day as keyof IScheduleRequest] = [
          {
            open: twentyFourSlot.open,
            close: twentyFourSlot.close,
          },
        ];
      }
      continue;
    }
    for (const slot of slots) {
      if (slot.open && slot.close) {
        // if there's any slots where the start is before midnight and the end is after midnight,
        // we need to split them into two days
        if (isSlotSplitNeeded(slot)) {
          const firstSlot = {
            open: slot.open,
            close: "23:59",
          };
          const secondSlot = {
            open: "00:00",
            close: slot.close,
          };
          newSchedule[day as keyof IScheduleRequest].push(firstSlot);
          const nextDay = daysOfTheWeek[(index + 1) % daysOfTheWeek.length];
          newSchedule[nextDay as keyof IScheduleRequest].push(secondSlot);
        } else {
          newSchedule[day as keyof IScheduleRequest].push({
            open: slot.open,
            close: slot.close,
          });
        }
      }
    }
  }

  return mergeOverlappingSlots(newSchedule);
}

function toFrontendSchedule(
  schedule: IScheduleResponse | null
): ISchedule | null {
  if (!schedule) return null;

  const newSchedule: ISchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  // Create a mutable copy of the schedule to track modifications
  const mutableSchedule = { ...schedule };

  for (const [index, day] of daysOfTheWeek.entries()) {
    const currentDaySlots =
      mutableSchedule[day as keyof IScheduleResponse] || [];
    const nextDay = daysOfTheWeek[(index + 1) % daysOfTheWeek.length];
    // For Sunday, use the already processed slots from newSchedule for Monday
    const nextDaySlots =
      day === "sunday"
        ? newSchedule.monday
        : mutableSchedule[nextDay as keyof IScheduleResponse] || [];
    const processedSlots: ITimeSlot[] = currentDaySlots.map(
      (slot: ITimeSlotResponse, slotIndex: number) => ({
        id: `${day}-${slotIndex}`,
        open: slot.open as TimeString,
        close: slot.close as TimeString,
      })
    );

    for (let i = processedSlots.length - 1; i >= 0; i--) {
      const currentSlot = processedSlots[i];
      if (currentSlot.close === "23:59") {
        const nextDayStartSlot = nextDaySlots.find(
          (slot) => slot.open === "00:00"
        );
        if (nextDayStartSlot) {
          // set the current day's close to the overflow slot's close
          currentSlot.close = nextDayStartSlot.close as TimeString;
          // set the current day's close to the overflow slot's close
          if (day === "sunday") {
            // Update the already processed Monday slots in newSchedule
            newSchedule.monday = nextDaySlots.filter(
              (slot) => slot.open !== "00:00"
            ) as ITimeSlot[];
          } else {
            // Update mutableSchedule for other days
            mutableSchedule[nextDay as keyof IScheduleResponse] =
              nextDaySlots.filter(
                (slot) => slot.open !== "00:00"
              ) as ITimeSlotResponse[];
          }
        }
      }
    }

    newSchedule[day as keyof ISchedule] = processedSlots;
  }

  return newSchedule;
}

function toBackendFaqs(faqs: IFAQ[]): IFAQRequest[] {
  // if the faq is a new locally made one, remove the ID before sending to backend
  return faqs.map(({ id, ...tail }) => {
    if (id.startsWith(LOCAL_ID_PREFIX)) {
      return tail;
    }
    return { id, ...tail };
  });
}

function toBackendOrderingConfig(
  orderingConfig: Partial<IOrderingConfig>
): IOrderingConfigRequest {
  const backendOrderingConfig: Partial<IOrderingConfigRequest> = {};

  if (
    orderingConfig.prepTime !== undefined &&
    orderingConfig.prepTime !== null &&
    orderingConfig.prepTime !== ""
  ) {
    backendOrderingConfig.prepTime = minutesToISO8601Duration(
      parseInt(orderingConfig.prepTime)
    );
  }

  if (
    orderingConfig.allowScheduledOrders !== undefined &&
    orderingConfig.allowScheduledOrders !== null
  ) {
    backendOrderingConfig.allowScheduledOrders =
      orderingConfig.allowScheduledOrders;
  }

  if (
    orderingConfig.orderUntilClosed !== undefined &&
    orderingConfig.orderUntilClosed !== null
  ) {
    backendOrderingConfig.orderUntilClosed = orderingConfig.orderUntilClosed;
  }

  if (
    orderingConfig.shouldTotal !== undefined &&
    orderingConfig.shouldTotal !== null
  ) {
    backendOrderingConfig.shouldTotal = orderingConfig.shouldTotal;
  }

  if (
    orderingConfig.exactTimeEstimate !== undefined &&
    orderingConfig.exactTimeEstimate !== null
  ) {
    backendOrderingConfig.exactTimeEstimate = orderingConfig.exactTimeEstimate;
  }

  if (orderingConfig.pickupModuleConfig !== undefined) {
    backendOrderingConfig.pickupModuleConfig = orderingConfig.pickupModuleConfig
      ? {
          paymentModuleConfig:
            orderingConfig.pickupModuleConfig.paymentModuleConfig,
        }
      : null;
  }

  if (orderingConfig.deliveryModuleConfig !== undefined) {
    backendOrderingConfig.deliveryModuleConfig =
      orderingConfig.deliveryModuleConfig
        ? {
            paymentModuleConfig:
              orderingConfig.deliveryModuleConfig.paymentModuleConfig,
          }
        : null;
  }

  if (orderingConfig.dynamicPrepTime !== undefined) {
    backendOrderingConfig.dynamicPrepTime = orderingConfig.dynamicPrepTime;
  }

  return backendOrderingConfig;
}

function toFrontendPrepTimeSchedule(
  schedule: IPrepTimeScheduleResponse
): IPrepTimeSchedule {
  const newSchedule: IPrepTimeSchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };

  // Create a mutable copy of the schedule to track modifications
  const mutableSchedule = { ...schedule };

  for (const [index, day] of daysOfTheWeek.entries()) {
    const currentDaySlots =
      mutableSchedule[day as keyof IScheduleResponse] || [];
    const nextDay = daysOfTheWeek[(index + 1) % daysOfTheWeek.length];
    // For Sunday, use the already processed slots from newSchedule for Monday
    const nextDaySlots =
      day === "sunday"
        ? newSchedule.monday
        : mutableSchedule[nextDay as keyof IScheduleResponse] || [];
    const processedSlots: IPrepTimeSlot[] = currentDaySlots.map(
      (slot: IPrepTimeSlotResponse, slotIndex: number) => ({
        ...slot,
        id: `${day}-${slotIndex}`,
        open: slot.open as TimeString,
        close: slot.close as TimeString,
      })
    );

    for (let i = processedSlots.length - 1; i >= 0; i--) {
      const currentSlot = processedSlots[i];
      if (currentSlot.close === "23:59") {
        const nextDayStartSlot = nextDaySlots.find(
          (slot) => slot.open === "00:00"
        );
        if (nextDayStartSlot) {
          // set the current day's close to the overflow slot's close
          currentSlot.close = nextDayStartSlot.close as TimeString;
          // set the current day's close to the overflow slot's close
          if (day === "sunday") {
            // Update the already processed Monday slots in newSchedule
            newSchedule.monday = nextDaySlots.filter(
              (slot) => slot.open !== "00:00"
            ) as IPrepTimeSlot[];
          } else {
            // Update mutableSchedule for other days
            mutableSchedule[nextDay as keyof IScheduleResponse] =
              nextDaySlots.filter(
                (slot) => slot.open !== "00:00"
              ) as IPrepTimeSlotResponse[];
          }
        }
      }
    }

    newSchedule[day as keyof ISchedule] = processedSlots;
  }

  const mapDay = (daySlots: IPrepTimeSlot[], day: string) =>
    daySlots.map(
      (slot, idx): IPrepTimeSlot => ({
        id: `${day}-${idx}`,
        open: (slot.open as TimeString) || null,
        close: (slot.close as TimeString) || null,
        delivery: slot.delivery
          ? ISO8601DurationToMinutes(slot.delivery).toString()
          : null,
        pickup: slot.pickup
          ? ISO8601DurationToMinutes(slot.pickup).toString()
          : null,
      })
    );
  return {
    monday: mapDay(newSchedule.monday, "monday"),
    tuesday: mapDay(newSchedule.tuesday, "tuesday"),
    wednesday: mapDay(newSchedule.wednesday, "wednesday"),
    thursday: mapDay(newSchedule.thursday, "thursday"),
    friday: mapDay(newSchedule.friday, "friday"),
    saturday: mapDay(newSchedule.saturday, "saturday"),
    sunday: mapDay(newSchedule.sunday, "sunday"),
  };
}

function toBackendPrepTimeSchedule(
  schedule: IPrepTimeSchedule | null | undefined
): IPrepTimeScheduleRequest | null | undefined {
  if (schedule == null) return schedule as null | undefined;

  const newSchedule: IPrepTimeSchedule = {
    monday: [],
    tuesday: [],
    wednesday: [],
    thursday: [],
    friday: [],
    saturday: [],
    sunday: [],
  };
  for (const [index, day] of daysOfTheWeek.entries()) {
    const slots = schedule[day as DayOfWeek];
    const has24HourSlot = slots.some(
      (slot) =>
        slot.open &&
        slot.close &&
        slot.open === slot.close &&
        slot.open != "00:00"
    );
    if (has24HourSlot) {
      // If there's a 24-hour slot, only include that one
      const twentyFourSlot = slots.find(
        (slot) =>
          slot.open &&
          slot.close &&
          slot.open === slot.close &&
          slot.open !== "00:00"
      );
      if (twentyFourSlot && twentyFourSlot.open && twentyFourSlot.close) {
        newSchedule[day as DayOfWeek] = [
          {
            ...twentyFourSlot,
            open: twentyFourSlot.open,
            close: twentyFourSlot.close,
          },
        ];
      }
      continue;
    }
    for (const slot of slots) {
      if (slot.open && slot.close) {
        // if there's any slots where the start is before midnight and the end is after midnight,
        // we need to split them into two days
        if (isSlotSplitNeeded(slot)) {
          const firstSlot = {
            ...slot,
            open: slot.open,
            close: "23:59" as const,
          };
          const secondSlot = {
            ...slot,
            open: "00:00" as const,
            close: slot.close,
          };
          newSchedule[day as DayOfWeek].push(firstSlot);
          const nextDay = daysOfTheWeek[(index + 1) % daysOfTheWeek.length];
          newSchedule[nextDay as DayOfWeek].push(secondSlot);
        } else {
          newSchedule[day as DayOfWeek].push({
            ...slot,
            open: slot.open,
            close: slot.close,
          });
        }
      }
    }
  }

  const mapDay = (daySlots: IPrepTimeSlot[]) =>
    daySlots
      .filter((s) => s.open && s.close) // only send complete slots
      .map(
        (slot): IPrepTimeSlotRequest => ({
          open: slot.open!,
          close: slot.close!,
          delivery: slot.delivery
            ? minutesToISO8601Duration(parseInt(slot.delivery))
            : null,
          pickup: slot.pickup
            ? minutesToISO8601Duration(parseInt(slot.pickup))
            : null,
        })
      );
  return {
    monday: mapDay(newSchedule.monday),
    tuesday: mapDay(newSchedule.tuesday),
    wednesday: mapDay(newSchedule.wednesday),
    thursday: mapDay(newSchedule.thursday),
    friday: mapDay(newSchedule.friday),
    saturday: mapDay(newSchedule.saturday),
    sunday: mapDay(newSchedule.sunday),
  };
}
